import { DuetHeapEvents, SierraHeapEvents } from "../../types/heap.type"
import { returnUrlWithPrefix } from "../general_utils"

type addPrefix<TKey, TPrefix extends string> = TKey extends string
  ? `${TPrefix}${TKey}`
  : never

type EventLocation =
  | "Support Homepage"
  | "Help Article"
  | "404"
  | "Banner"
  | "CUF"
  | "Search Results"
  | "All"

export type EventName =
  | "Home Page"
  | "Article"
  | "404"
  | "Banner"
  | "CUF"
  | "Search Results"
  | "Article Visited"
  | "User Profile"
  | "Scroll Depth"
  | "Has Event Within 24 Hours"
  | SierraHeapEvents
  | DuetHeapEvents

type PageArea = "header" | "body" | "footer" | "fixed-bottom" | "chatbot"

export type ArticleData = {
  requiresAssistance: boolean
  helpArticleAssistanceCTAs: string | null
  helpArticleCategory: string
  helpArticleSubcategory: string
  articleId: number
}

export type CustomHeapEvent = {
  EventLocation: EventLocation
  EventName: EventName
  Component: string
  PageArea: PageArea
  /**
   * Value seems to be the name of the component clicked. E.G. 'Language Selector' or "Tab"
   */
  PropertyValue: string
  Value: string | number
  requiresAssistance?: boolean
  helpArticleAssistanceCTAs?: string | null
  helpArticleCategory?: string
  helpArticleSubcategory?: string
  scrollDepthPercent?: number
  [key: string]: unknown
}

export type CustomHeapUserData = {
  EventLocation: EventLocation
  EventName: EventName
  assortmentPlan: string
  orgId: string
  userId: string
  isFree: boolean
  isOrganizer: boolean
  isNewAccount: boolean
  isHighVAlue: boolean
}

export type HeapElementAttributes = {
  "data-eventlocation": EventLocation
  "data-eventname": addPrefix<EventName, "Help Center - ">
  "data-value": string | number
  "data-propertyvalue": string
  "data-pagearea": PageArea
}

export const mapEventToAttributes = (
  event: CustomHeapEvent
): HeapElementAttributes => {
  return {
    "data-eventlocation": event.EventLocation,
    "data-eventname": `Help Center - ${event.EventName}`,
    "data-value": event.Value,
    "data-propertyvalue": event.PropertyValue,
    "data-pagearea": event.PageArea
  }
}

export const getLocationAndNameByUrlPath = (): Pick<
  CustomHeapEvent,
  "EventLocation" | "EventName"
> | null => {
  if (typeof window !== "undefined") {
    const path = window?.location?.pathname || ""

    if (
      path === `${returnUrlWithPrefix("/")}/` ||
      path === `${returnUrlWithPrefix("/help")}/`
    ) {
      return {
        EventLocation: "Support Homepage",
        EventName: "Home Page"
      }
    }

    if (path?.includes("/articles/")) {
      return {
        EventLocation: "Help Article",
        EventName: "Article"
      }
    }

    if (path.includes("/contactus") || path?.includes("/contact-us")) {
      return {
        EventLocation: "CUF",
        EventName: "CUF"
      }
    }

    if (path.includes("/search") && window?.location?.search?.includes("q=")) {
      return {
        EventLocation: "Search Results",
        EventName: "Search Results"
      }
    }

    // if one of these doesn't match we make the assumption it's a 404
    return {
      EventLocation: "404",
      EventName: "404"
    }
  }

  return null
}
